/**
 * ActionBar — the standard action section every screen uses (list, create, edit, view,
 * settings, dashboard). Mirrors the OneInvoice panel head:
 *
 *  ┌ Title  [status]                                        [Total (N) ▾] [▽ Advanced search] ┐
 *  │ [⊞][↻][←]  [🔍 Search …]  [+ New] [Upload] [Import] [Export ▾] │ [View] [Edit] [Print]  │
 *  │ (filters panel, when Advanced search is open)                                            │
 *  └──────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * Props
 *  title, subtitle          heading (string | node)
 *  badge                    node rendered right after the title (StatusBadge etc.)
 *  tools                    icon-only buttons at the far left:
 *                           [{ key, icon, onClick, title, disabled, active, node }]  (`node` = custom element)
 *  search                   { value, onChange, placeholder, width } | null
 *  primary                  main actions: [{ key, label, icon, onClick, variant, disabled, hidden, testid, node }]
 *                           variant: 'primary' (default navy) | 'outline' | 'destructive' | 'ghost'
 *  secondary                context actions after a divider (usually disabled until applicable); same shape
 *  rightSlot                node(s) on the title row's right (Total, view switcher …)
 *  advancedSearch           node — filter panel; a "▽ Advanced search" toggle is rendered on the title row
 *  activeFilterCount        number badge on that toggle
 *  advancedOpen/onAdvancedToggle  controlled mode for the panel (optional)
 *  trailing                 node at the end of the toolbar line (e.g. "3 selected · clear")
 *  sticky                   keep the bar pinned while the page scrolls (record pages)
 *  bordered                 draw the panel border/background (false when embedded in DataGrid)
 */
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { MagnifyingGlass, Funnel, X } from '@phosphor-icons/react';

const VARIANT = {
  primary: 'default',
  outline: 'outline',
  destructive: 'destructive',
  ghost: 'ghost',
  secondary: 'secondary',
};

export function ActionButton({ action, size = 'sm' }) {
  if (!action || action.hidden) return null;
  if (action.node) return action.node;
  const Icon = action.icon;
  return (
    <Button
      variant={VARIANT[action.variant] || (action.variant === undefined ? 'default' : action.variant)}
      size={size}
      /* Smaller on a phone. At full size two or three actions wrapped onto a line each
         and the top of every screen became a scattered stack of buttons before any
         content; at this size they sit on one row and still take a thumb. */
      className={`h-8 px-2.5 text-[12px] sm:h-9 sm:px-3 sm:text-sm ${action.className || ''}`}
      disabled={action.disabled}
      onClick={action.onClick}
      title={action.title}
      data-testid={action.testid || `action-${action.key}`}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {action.label}
    </Button>
  );
}

export function ToolButton({ tool }) {
  if (!tool || tool.hidden) return null;
  if (tool.node) return tool.node;
  const Icon = tool.icon;
  return (
    <Button
      variant="outline"
      size="icon"
      className={`h-8 w-8 sm:h-9 sm:w-9 ${tool.active ? 'bg-shell-primary text-white border-shell-primary hover:bg-shell-primary/90 hover:text-white' : ''} ${tool.className || ''}`}
      onClick={tool.onClick}
      disabled={tool.disabled}
      title={tool.title}
      aria-label={tool.title}
      data-testid={tool.testid || `tool-${tool.key}`}
    >
      {Icon && <Icon className={`w-4 h-4 ${tool.spin ? 'animate-spin' : ''}`} />}
    </Button>
  );
}

export default function ActionBar({
  title,
  subtitle,
  badge,
  tools = [],
  search = null,
  primary = [],
  secondary = [],
  rightSlot = null,
  advancedSearch = null,
  activeFilterCount = 0,
  advancedOpen,
  onAdvancedToggle,
  trailing = null,
  sticky = false,
  bordered = true,
  className = '',
  'data-testid': testId = 'action-bar',
}) {
  const [openLocal, setOpenLocal] = useState(false);
  const isOpen = advancedOpen ?? openLocal;
  const toggleAdvanced = () => (onAdvancedToggle ? onAdvancedToggle(!isOpen) : setOpenLocal((v) => !v));

  const visiblePrimary = primary.filter((a) => a && !a.hidden);
  const visibleSecondary = secondary.filter((a) => a && !a.hidden);
  const hasTitleRow = title || badge || rightSlot || advancedSearch;

  return (
    <div
      className={`${bordered ? 'bg-white border border-border rounded-lg shadow-sm' : ''} ${sticky ? 'sticky top-0 z-20' : ''} ${className}`}
      data-testid={testId}
    >
      {hasTitleRow && (
        <div className="flex items-center gap-3 px-4 pt-3 pb-2 flex-wrap">
          {(title || subtitle) && (
            <div className="min-w-0 mr-auto">
              <div className="flex items-center gap-2 flex-wrap">
                {title && <h2 className="font-heading text-lg font-semibold text-foreground leading-tight truncate" data-testid="action-bar-title">{title}</h2>}
                {badge}
              </div>
              {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
            </div>
          )}
          {!title && !subtitle && <div className="mr-auto" />}
          {rightSlot}
          {advancedSearch && (
            <Button
              size="sm"
              variant={isOpen ? 'default' : 'outline'}
              className="h-8"
              onClick={toggleAdvanced}
              data-testid="grid-advanced-search"
            >
              <Funnel className="w-3.5 h-3.5" /> Advanced search
              {activeFilterCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-white text-[10px]">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          )}
        </div>
      )}

      <div className={`flex items-center gap-2 px-4 ${hasTitleRow ? 'pb-3' : 'py-3'} flex-wrap`} data-testid="action-bar-toolbar">
        {tools.map((t) => <ToolButton key={t.key} tool={t} />)}

        {search && (
          /* Full width on a phone — a 224px box next to the buttons left a search field
             you could not read what you had typed into. Its set width returns at sm. */
          <div className="relative w-full sm:w-auto" style={{ '--grid-search-w': `${search.width || 224}px` }}>
            <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              placeholder={search.placeholder || 'Search'}
              className="grid-search h-8 sm:h-9 pl-8 pr-7 rounded-md border border-input bg-background text-sm outline-none focus:ring-1 focus:ring-ring"
              data-testid={search.testid || 'grid-search'}
            />
            {search.value && (
              <button type="button" onClick={() => search.onChange('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Clear search">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}

        {visiblePrimary.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            {visiblePrimary.map((a) => <ActionButton key={a.key} action={a} />)}
          </div>
        )}

        {visibleSecondary.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap md:ml-1 md:pl-3 md:border-l border-border">
            {visibleSecondary.map((a) => <ActionButton key={a.key} action={{ variant: 'outline', ...a }} />)}
          </div>
        )}

        {trailing && <div className="ml-auto text-xs text-muted-foreground">{trailing}</div>}
      </div>

      {isOpen && advancedSearch && (
        <div className="mx-4 mb-3 p-3 rounded-md border border-dashed border-border bg-muted/30" data-testid="grid-advanced-panel">
          {advancedSearch}
        </div>
      )}
    </div>
  );
}
